// Rapport téléchargeable du calculateur : une page HTML autonome (styles intégrés, aux couleurs
// du site, imprimable en PDF), avec sa version Markdown à l'intérieur (pour un ticket ou une IA).
// Les deux formats lisent les mêmes données, préparées et mises en forme par le calculateur :
// ce module ne calcule rien, il met en page.

export type ReportLink = { label: string; url: string };
type Status = "ok" | "tight" | "oom";

export type ReportData = {
  /** « October 7, 2026 ». */
  generated: string;
  /** Lien qui rouvre exactement cette configuration. */
  configUrl: string;
  model: {
    name: string;
    isNew: boolean;
    origin?: { lab: string; country?: string; flag?: string };
    parent?: ReportLink;
    /** « 27.4B parameters », « 256K context », « Apache 2.0, commercial use allowed », « Released … ». */
    facts: string[];
    arch: string;
    preview?: string;
    popularity?: string;
    runsWith?: string;
    ollama?: string;
    links: ReportLink[];
  };
  answer: { status: Status; statusLabel: string; headline: string; cost?: string; lines: string[] };
  memory: { parts: { key: string; label: string; gib: number }[]; needed: number; usable: number; free: number };
  /** tiles : les chiffres (HTML) ; lines : les mêmes en phrases (Markdown) ; verdict : auto-hébergement ou API. */
  speed?: { tiles: { value: string; label: string }[]; lines: string[]; verdict?: string };
  setup: [string, string][];
  capacity?: string[];
  quant?: { title: string; rows: [string, string, string, string][] };
  options?: [string, string, string][];
  rent?: { date: string; rows: { name: string; price: string; url: string; sponsored: boolean }[] };
  deploy?: { engine: string; command: string };
};

const AFFILIATE = "Some rental links are affiliate links: StudioTV may earn a commission, at no extra cost to you.";
const DISCLAIMER = "Estimates, not guarantees: actual memory depends on the engine version, kernels and settings. Keep some headroom and confirm with nvidia-smi.";
const CALCULATOR = "https://studiotvai.com/llm-vram-calculator";
const g2 = (x: number) => x.toFixed(2);

// ── Markdown ────────────────────────────────────────────────────────────────

export function reportMarkdown(d: ReportData): string {
  const row = (...cells: string[]) => `| ${cells.join(" | ")} |`;
  const m = d.model;
  return [
    `# LLM VRAM report: ${m.name}`,
    "",
    `Generated on ${d.generated} with the StudioTV LLM VRAM Calculator.`,
    `Open this configuration: ${d.configUrl}`,
    "",
    "## Model",
    "",
    ...(m.origin ? [`- Made by ${m.origin.lab}${m.origin.country ? `, ${m.origin.country}` : ""}`] : []),
    ...(m.parent ? [`- Fine-tune of ${m.parent.label} (${m.parent.url})`] : []),
    ...m.facts.map((f) => `- ${f}`),
    `- Architecture: ${m.arch}`,
    ...(m.popularity ? [`- ${m.popularity}`] : []),
    ...(m.runsWith ? [`- Runs with: ${m.runsWith}`] : []),
    ...(m.preview ? [`- Note: ${m.preview}`] : []),
    "",
    "## Answer",
    "",
    `**${d.answer.headline}**: ${d.answer.statusLabel.toLowerCase()}.`,
    ...(d.answer.cost ? [`${d.answer.cost}.`] : []),
    ...d.answer.lines,
    ...(d.speed ? ["", "## Speed & cost (rough estimate)", "", ...[...d.speed.lines, ...(d.speed.verdict ? [d.speed.verdict] : [])].map((l) => `- ${l}`)] : []),
    ...(d.deploy ? [
      "", `## Deploy (${d.deploy.engine})`, "", "```bash", d.deploy.command, "```",
      ...(m.ollama ? ["", `With Ollama: \`${m.ollama}\``] : []),
    ] : []),
    "",
    "## Configuration",
    "",
    row("Setting", "Value"), row("---", "---"),
    ...d.setup.map(([k, v]) => row(k, v)),
    "",
    "## Memory per GPU",
    "",
    row("Part", "GiB"), row("---", "---:"),
    ...d.memory.parts.map((p) => row(p.label, g2(p.gib))),
    row("**Needed**", `**${g2(d.memory.needed)}**`),
    row("Usable", g2(d.memory.usable)),
    row(d.memory.free >= 0 ? "Headroom" : "Missing", g2(Math.abs(d.memory.free))),
    ...(d.capacity ? ["", "## Capacity", "", ...d.capacity.map((l) => `- ${l}`)] : []),
    ...(d.quant ? [
      "", `## ${d.quant.title}`, "",
      row("Weights", "Quality", "Size", "GPUs needed"), row("---", "---", "---:", "---:"),
      ...d.quant.rows.map((r) => row(...r)),
    ] : []),
    ...(d.options?.length ? [
      "", "## Cheapest GPUs that fit in one server", "",
      row("Setup", "Price", "Provider"), row("---", "---:", "---"),
      ...d.options.map((r) => row(...r)),
    ] : []),
    ...(d.rent?.rows.length ? [
      "", `## Where to rent it (on-demand, prices of ${d.rent.date})`, "",
      ...d.rent.rows.map((r) => `- ${r.name}: ${r.price} (${r.url})`),
      ...(d.rent.rows.some((r) => r.sponsored) ? ["", AFFILIATE] : []),
    ] : []),
    ...(m.links.length ? ["", "## Links", "", ...m.links.map((l) => `- ${l.label}: ${l.url}`)] : []),
    "",
    "---",
    "",
    DISCLAIMER,
    "",
  ].join("\n");
}

// ── HTML ────────────────────────────────────────────────────────────────────

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
// Seulement des liens http(s) : une adresse « javascript: » venue d'une source externe ne devient jamais un lien.
const link = (l: ReportLink, cls = "") => /^https?:\/\//i.test(l.url)
  ? `<a${cls ? ` class="${cls}"` : ""} href="${esc(l.url)}">${esc(l.label)}</a>` : esc(l.label);
const table = (head: string[], rows: string[][], num: number[] = []) =>
  `<table><thead><tr>${head.map((h, i) => `<th${num.includes(i) ? ' class="num"' : ""}>${esc(h)}</th>`).join("")}</tr></thead><tbody>` +
  rows.map((r) => `<tr>${r.map((c, i) => `<td${num.includes(i) ? ' class="num"' : ""}>${esc(c)}</td>`).join("")}</tr>`).join("") +
  "</tbody></table>";

const STYLE = `
:root{--bg:#0b0f16;--panel:rgba(12,16,26,.9);--ink:#f4f7ff;--muted:rgba(244,247,255,.66);--line:rgba(110,211,255,.2);--a1:#49f2c2;--a2:#6ed3ff;--a3:#ffb45e;--bad:#ff6b6b}
*{box-sizing:border-box}
body{margin:0;color:var(--ink);font:15px/1.55 "Sora","Space Grotesk",system-ui,-apple-system,"Segoe UI",sans-serif;
background:radial-gradient(1100px 520px at 0% -10%,rgba(73,242,194,.13),transparent 60%),radial-gradient(900px 480px at 100% 0%,rgba(110,211,255,.12),transparent 60%),var(--bg)}
main{max-width:900px;margin:0 auto;padding:28px 16px 48px;display:grid;gap:18px}
a{color:var(--a2)}
.top{display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap}
.brand{font-weight:700;letter-spacing:.02em}
.brand span{background:linear-gradient(120deg,var(--a1),var(--a2));-webkit-background-clip:text;background-clip:text;color:transparent}
.print{font:inherit;font-size:14px;font-weight:600;color:var(--ink);background:rgba(73,242,194,.12);border:1px solid rgba(73,242,194,.45);border-radius:999px;padding:8px 16px;cursor:pointer}
.card{background:var(--panel);border:1px solid var(--line);border-radius:18px;padding:20px 22px;min-width:0}
h1{font-size:clamp(28px,6vw,42px);line-height:1.15;margin:6px 0 4px}
h2{font-size:12px;letter-spacing:.22em;text-transform:uppercase;color:var(--muted);margin:0 0 12px;font-weight:600}
p{margin:0}
.muted{color:var(--muted)}
.origin img{width:1.25em;height:.94em;vertical-align:-.12em;margin-right:.45em;border-radius:2px}
.new{display:inline-block;vertical-align:middle;margin-left:10px;background:var(--a3);color:#1d1205;font-size:12px;font-weight:800;letter-spacing:.06em;padding:2px 9px;border-radius:999px}
.facts{display:flex;flex-wrap:wrap;gap:8px;list-style:none;margin:14px 0 10px;padding:0}
.facts li{border:1px solid var(--line);border-radius:999px;padding:4px 12px;font-size:13px}
.warn{color:var(--a3);margin-top:8px}
.answer{border-color:rgba(73,242,194,.45)}
.status{display:inline-block;font-size:12px;font-weight:800;letter-spacing:.14em;text-transform:uppercase;padding:3px 10px;border-radius:999px}
.status.ok{background:rgba(73,242,194,.16);color:var(--a1)}
.status.tight{background:rgba(255,180,94,.16);color:var(--a3)}
.status.oom{background:rgba(255,107,107,.16);color:var(--bad)}
.headline{font-size:clamp(24px,5vw,34px);font-weight:700;line-height:1.2;margin:10px 0 4px}
.cost{color:var(--a1);font-weight:700}
.bar{position:relative;display:flex;height:16px;border-radius:999px;background:rgba(244,247,255,.07);overflow:hidden;margin:18px 0 10px}
.bar span{height:100%}
.bar .limit{position:absolute;top:0;bottom:0;width:2px;background:var(--bad)}
.legend{display:flex;flex-wrap:wrap;gap:6px 18px;list-style:none;margin:0;padding:0;font-size:13px}
.legend i{display:inline-block;width:10px;height:10px;border-radius:3px;margin-right:6px}
.seg-weights{background:var(--a2)}.seg-kv{background:var(--a1)}.seg-state{background:#b18cff}.seg-optim{background:var(--a3)}.seg-acts{background:#ff7aa8}.seg-overhead{background:rgba(244,247,255,.35)}
.notes{margin:12px 0 0;padding-left:18px}
.notes li+li{margin-top:4px}
.tiles{display:grid;grid-template-columns:repeat(auto-fit, minmax(min(140px, 100%), 1fr));gap:12px}
.tile{border:1px solid var(--line);border-radius:14px;padding:12px 14px}
.tile b{display:block;font-size:22px;line-height:1.25}
.tile span{font-size:12px;color:var(--muted)}
.grid2{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,330px),1fr));gap:18px}
.scroll{overflow-x:auto}
table{width:100%;border-collapse:collapse;font-size:14px}
th,td{text-align:left;padding:8px 10px;border-bottom:1px solid rgba(255,255,255,.07);vertical-align:top}
th{color:var(--muted);font-weight:600;font-size:12px;text-transform:uppercase;letter-spacing:.08em}
.num{text-align:right;font-variant-numeric:tabular-nums;white-space:nowrap}
pre{margin:0;background:#05080d;border:1px solid var(--line);border-radius:12px;padding:14px;overflow-x:auto;font:13px/1.5 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}
code{font:13px ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;background:rgba(244,247,255,.08);padding:2px 6px;border-radius:6px}
dl{display:grid;grid-template-columns:max-content 1fr;gap:8px 16px;margin:14px 0 0}
dt{color:var(--muted)}dd{margin:0;min-width:0;overflow-wrap:anywhere}
.links{display:flex;flex-wrap:wrap;gap:8px}
.links a{color:var(--ink);text-decoration:none;font-weight:600;font-size:14px;border:1px solid var(--line);border-radius:999px;padding:8px 14px}
.links a.main{border-color:rgba(73,242,194,.5);background:rgba(73,242,194,.12)}
.rent li{display:flex;justify-content:space-between;gap:12px;padding:8px 0;border-bottom:1px solid rgba(255,255,255,.07)}
.rent{list-style:none;margin:0;padding:0}
footer{font-size:12px;color:var(--muted);display:grid;gap:6px}
details summary{cursor:pointer;color:var(--a2);font-weight:600}
details pre{margin-top:12px;white-space:pre-wrap}
@media (max-width:520px){.card{padding:16px}dl{grid-template-columns:1fr;gap:2px 0}dd{margin-bottom:8px}}
@media print{
:root{--ink:#0b0f16;--muted:#4b5563;--line:#d1d5db;--panel:#fff;--a1:#0b8a69;--a2:#0369a1;--a3:#b45309}
body{background:#fff;font-size:12px}
main{padding:0;gap:12px}
.card{break-inside:avoid;padding:14px 16px}
.print,details{display:none}
pre{background:#f6f8fa;white-space:pre-wrap}
code{background:#f1f5f9}
.bar,.legend i,.status,.new,.tile{-webkit-print-color-adjust:exact;print-color-adjust:exact}
.bar{background:#e5e7eb}
th,td,.rent li{border-color:#e5e7eb}
.links a.main{background:none}
}`;

export function reportHtml(d: ReportData): string {
  const m = d.model, a = d.answer, mem = d.memory;
  const scale = Math.max(mem.needed, mem.usable) || 1;
  const pct = (x: number) => `${((x / scale) * 100).toFixed(2)}%`;
  const card = (title: string, body: string, cls = "") => `<section class="card${cls ? ` ${cls}` : ""}"><h2>${esc(title)}</h2>${body}</section>`;

  const origin = (m.origin || m.parent)
    ? `<p class="origin muted">${m.origin ? `${m.origin.flag ? `<img src="${esc(m.origin.flag)}" alt="">` : ""}${esc(m.origin.lab)}${m.origin.country ? `, ${esc(m.origin.country)}` : ""}` : ""}` +
      `${m.origin && m.parent ? " · " : ""}${m.parent ? `fine-tune of ${link(m.parent)}` : ""}</p>`
    : "";

  const run = d.deploy || m.ollama || m.runsWith
    ? card("Run it", [
      m.runsWith ? `<p>Runs with <strong>${esc(m.runsWith)}</strong>.</p>` : "",
      d.deploy ? `<p class="muted" style="margin:12px 0 8px">Launch command (${esc(d.deploy.engine)}):</p><pre>${esc(d.deploy.command)}</pre>` : "",
      m.ollama ? `<p style="margin-top:12px">On your own machine: <code>${esc(m.ollama)}</code></p>` : "",
    ].join(""))
    : "";

  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>LLM VRAM report: ${esc(m.name)}</title>
<style>${STYLE}</style>
</head>
<body>
<main>
<div class="top">
<div class="brand">StudioTV · <span>LLM VRAM report</span></div>
<button class="print" type="button" onclick="window.print()">Print or save as PDF</button>
</div>

<header class="card">
${origin}
<h1>${esc(m.name)}${m.isNew ? '<span class="new">NEW</span>' : ""}</h1>
<ul class="facts">${m.facts.map((f) => `<li>${esc(f)}</li>`).join("")}</ul>
<p class="muted">${esc(m.arch)}</p>
${m.popularity ? `<p class="muted" style="margin-top:6px">${esc(m.popularity)}</p>` : ""}
${m.preview ? `<p class="warn">${esc(m.preview)}</p>` : ""}
</header>

<section class="card answer">
<h2>The answer</h2>
<span class="status ${a.status}">${esc(a.statusLabel)}</span>
<div class="headline">${esc(a.headline)}</div>
${a.cost ? `<p class="cost">${esc(a.cost)}</p>` : ""}
<div class="bar" role="img" aria-label="Memory per GPU: ${g2(mem.needed)} GiB needed, ${g2(mem.usable)} GiB usable">
${mem.parts.map((p) => `<span class="seg-${esc(p.key)}" style="width:${pct(p.gib)}"></span>`).join("")}
${mem.needed > mem.usable ? `<span class="limit" style="left:${pct(mem.usable)}"></span>` : ""}
</div>
<ul class="legend">${mem.parts.map((p) => `<li><i class="seg-${esc(p.key)}"></i>${esc(p.label)} ${g2(p.gib)} GiB</li>`).join("")}</ul>
<p class="muted" style="margin-top:10px">Per GPU: ${g2(mem.needed)} GiB needed of ${g2(mem.usable)} GiB usable, ${g2(Math.abs(mem.free))} GiB ${mem.free >= 0 ? "of headroom" : "missing"}.</p>
${a.lines.length ? `<ul class="notes">${a.lines.map((l) => `<li>${esc(l)}</li>`).join("")}</ul>` : ""}
</section>

${d.speed ? card("Speed & cost (rough estimate)",
    `<div class="tiles">${d.speed.tiles.map((t) => `<div class="tile"><b>${esc(t.value)}</b><span>${esc(t.label)}</span></div>`).join("")}</div>` +
    (d.speed.verdict ? `<p style="margin-top:12px">${esc(d.speed.verdict)}.</p>` : "")) : ""}

${run}

<div class="grid2">
${card("Your setup", `<div class="scroll">${table(["Setting", "Value"], d.setup)}</div>`)}
${d.capacity ? card("Capacity", `<ul class="notes" style="margin:0">${d.capacity.map((l) => `<li>${esc(l)}</li>`).join("")}</ul>`) : ""}
</div>

${d.quant ? card(d.quant.title, `<div class="scroll">${table(["Weights", "Quality", "Size", "GPUs"], d.quant.rows, [2, 3])}</div>`) : ""}
${d.options?.length ? card("Cheapest GPUs that fit in one server", `<div class="scroll">${table(["Setup", "Price", "Provider"], d.options, [1])}</div>`) : ""}
${d.rent?.rows.length ? card(`Where to rent it · prices of ${d.rent.date}`,
    `<ul class="rent">${d.rent.rows.map((r) => `<li><a href="${esc(r.url)}"${r.sponsored ? ' rel="sponsored"' : ""}>${esc(r.name)}</a><span class="num">${esc(r.price)}</span></li>`).join("")}</ul>` +
    (d.rent.rows.some((r) => r.sponsored) ? `<p class="muted" style="margin-top:10px;font-size:12px">${esc(AFFILIATE)}</p>` : "")) : ""}

${card("Links", `<div class="links">${link({ label: "Open this configuration", url: d.configUrl }, "main")}${m.links.map((l) => link(l)).join("")}</div>`)}

<footer>
<p>${esc(DISCLAIMER)}</p>
<p>Generated on ${esc(d.generated)} with the <a href="${CALCULATOR}">StudioTV LLM VRAM Calculator</a>.</p>
</footer>

<details class="card">
<summary>Markdown version, to paste into a ticket or an AI</summary>
<pre>${esc(reportMarkdown(d))}</pre>
</details>
</main>
</body>
</html>
`;
  return html;
}
